import { useEffect, useState } from "react";
import { Form, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { clearSharedAlert, SharedAlert } from "../../app/siteSlice";
import { CourseState, setCourse, setCourses, UserRole } from "../../app/userSlice";
import { scrollToSection } from "../../app/useScrollToSection";
import AlertMessage from "../../components/AlertMessage";
import BroadcastAlerts from "../../components/BroadcastAlerts";
import { Loading } from "../../components/Loading";
import NoDataTableBody from "../../components/NoDataTableBody";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import SimpleListbox, { SimpleOption } from "../../components/input/SimpleListbox";
import { IDotNetModelStateDictionary } from "../../interfaces/dotNetError";
import StudentDashboard from "../dashboard/Dashboard";
import { useGetDashboardInfoQuery } from "../dashboard/dashboardApiSlice";
import { lookupGradeByName } from "../lookup/lookupUtil";
import { useGetGradesQuery } from "../lookup/lookupApiSlice";
import AddCourseButton from "./AddCourse";
import {
	useGetCourseEnrollmentsQuery,
	useMakeCourseSelectionMutation,
	useRegisterForCourseMutation,
	useGetSelectCoursePageQuery,
} from "./coursesApiSlice";

export default function Courses() {
	const { firstName, lastName, courses, role, isTeachersWriterAccount, isCourseInTransition } =
		useAppSelector((state) => state.user);
	const { aspUrl, config, sharedAlert } = useAppSelector((state) => state.site);

	const [view, setView] = useState<string>("dashboard");
	const [showAll, setShowAll] = useState(false);
	const [displayShowAll, setDisplayShowAll] = useState(true);
	const [courseCode, setCourseCode] = useState("");
	const [courseCodeError, setCourseCodeError] = useState("");
	const [showSchools, setShowSchools] = useState(false);
	const [lastMessage, setLastMessage] = useState<SharedAlert>();

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const selectCoursePageResult = useGetSelectCoursePageQuery();
	const selectCoursePageData = selectCoursePageResult.data;

	const coursesResult = useGetCourseEnrollmentsQuery(true);
	const courseRegistrations = coursesResult.data;

	const dashboardResult = useGetDashboardInfoQuery(showAll);
	const dashboardCourses = dashboardResult.data;

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [selectCourse, selectCourseResult] = useMakeCourseSelectionMutation();

	const [register, registerResult] = useRegisterForCourseMutation();

	if (sessionStorage.getItem("prefersSpa") === "false") {
		window.location.href = `${aspUrl}/SignIn/SelectCourse`;
	}

	const handleRegister = async (event: React.FormEvent) => {
		event.preventDefault();
		if (courseCode) {
			setCourseCodeError("");
			register(courseCode)
				.unwrap()
				.then((payload) => {
					setCourseCode("");
				})
				.catch((error) => handleRegisterError(error));
		}
	};

	useEffect(() => {
		if (sharedAlert) {
			setLastMessage(sharedAlert);
			dispatch(clearSharedAlert());
		}
	}, [sharedAlert]);

	useEffect(() => {
		if (role == UserRole.Proctor) {
			setView("table");
		}
		if (courseRegistrations && grades) {
			const courses: CourseState[] = courseRegistrations.courses.map((c) => {
				return {
					id: c.courseId,
					name: c.courseName,
					gradeId: lookupGradeByName(c.courseGrade, grades)?.gradeId ?? 0,
					isActive: c.status === "Active",
					isFavorite: c.isFavorite,
				};
			});
			dispatch(setCourses(courses));

			const hasActiveCourse = courses.some((course) => course.isActive);
			const hasInactiveCourse = courses.some((course) => !course.isActive);
			setDisplayShowAll(hasActiveCourse && hasInactiveCourse);
			if (hasInactiveCourse && !hasActiveCourse) {
				setShowAll(true);
			}

			setShowSchools(
				[...new Set(courseRegistrations.courses.map((c) => c.school))].length > 1
			);
		}
	}, [courseRegistrations, grades]);

	interface IMutationError {
		status: number;
		data: IDotNetModelStateDictionary;
	}

	const handleRegisterError = (error: IMutationError) => {
		const courseCodeErrors = error.data["courseCode"]?.errors;
		if (courseCodeErrors?.length) {
			const msg = courseCodeErrors[0].errorMessage;
			setCourseCodeError(`Error: ${msg}`);
		} else {
			setCourseCodeError(`Error: an unexpected error occurred.`);
		}
	};

	const handleSelectCourse = (id: number, name: string, grade: string, status: string) => {
		if (!grades) return;
		const isActive = status === "Active";
		selectCourse(id)
			.unwrap()
			.then((showAvatars) => {
				dispatch(
					setCourse({
						id,
						name,
						gradeId: lookupGradeByName(grade, grades)?.gradeId ?? 0,
						isActive: isActive,
						showUserAvatars: showAvatars,
					})
				);
				isActive ? navigate("/home") : navigate("/reports");
			})
			.catch((error) => handleSelectCourseError(error));
	};

	const handleSelectCourseError = (error: IMutationError) => {
		if (error.data) {
			setCourseCodeError(`Error: ${error.data}`);
		} else {
			setCourseCodeError(`Error: an unexpected error occurred.`);
		}
	};

	const filteredCourseRegistrations = courseRegistrations?.courses.filter(
		(course) => (course.status !== "Active") === showAll
	);

	const studentViewFilterOptions: SimpleOption[] = [
		{ value: "dashboard", label: "Dashboard" },
		{ value: "table", label: "Tables" },
	];

	if (config?.isDevEnvironment) {
		studentViewFilterOptions.push({ value: "raw", label: "Raw Data" });
	}

	const courseStatusOptions: SimpleOption[] = [
		{ value: "false", label: "Active Courses" },
		{ value: "true", label: view == "dashboard" ? "Inactive Courses" : "All Courses" },
	];

	if (isCourseInTransition) return null;

	return (
		<>
			<BroadcastAlerts className="w-full" alerts={selectCoursePageData?.alerts} />
			{lastMessage && (
				<AlertMessage
					className="mb-8"
					type={lastMessage.level}
					message={lastMessage.message}
					showIcon={lastMessage.showIcon}
					onClear={() => setLastMessage(undefined)}
				/>
			)}
			<section className="text-s sm:text-left w-full">
				<div className="flex justify-between">
					<div className="w-3/4">
						<h1 className="font-bold">
							Welcome, {firstName} {lastName}
							{isTeachersWriterAccount ? " (Student)" : ""}!
						</h1>
					</div>
					{role === UserRole.Writer && (
						<SimpleListbox
							id="viewFilter"
							options={studentViewFilterOptions}
							value={view}
							className={"w-1/4"}
							onChange={(e) => setView(e.target.value)}
						/>
					)}
				</div>
				<h2 className="text-s sm:text-left">Select a Course</h2>
				<Greeting
					role={role}
					view={view}
					hasCourse={Boolean(courses?.length)}
					showInactiveCourses={showAll}
					showAddCourseLink={selectCoursePageData?.showAddCourseLink ?? false}
				/>
			</section>
			<section className="mt-8">
				{view !== "raw" && (
					<div className="flex flex-row justify-between">
						<div>
							{role === UserRole.Proctor &&
								selectCoursePageData?.showAddCourseLink && <AddCourseButton />}
						</div>
						<div>
							{displayShowAll && (
								<button
									id="show-all-courses-button"
									className="btn btn-neutral"
									onClick={() => setShowAll(!showAll)}>
									{showAll ? "Show Active Courses" : "Show Inactive Courses"}
								</button>
							)}
						</div>
						{/* 
                        <div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="courseStatusToggle"
                                    value=""
                                    className="sr-only peer"
                                    checked={showAll}
                                    onChange={() => setShowAll(!showAll)}
                                />
                                <div className="contrast-exclude w-11 h-6 bg-gray-200 peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:!bg-blue-600"></div>
                                <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                                    {view == "dashboard" ? "Show inactive" : "Show all"}
                                </span>
                            </label>
                        </div>
                        <div className="w-1/2 sm:w-1/3 md:w-1/4 mr-8 sm:mr-0">
                            <label
                                htmlFor="courseStatusFilter"
                                className="mb-2 text-sm font-medium text-gray-90">
                                Status Filter
                            </label>
                            <SimpleListbox
                                id="courseStatusFilter"
                                className={"w-full"}
                                options={courseStatusOptions}
                                value={showAll.toString()}
                                onChange={(e) => handleOptionsChange(e.target.value)}
                            />
                        </div>
                        */}
					</div>
				)}
				{view === "table" && (
					<table className="table-auto tbl tbl-data mt-4">
						<thead>
							<tr className="">
								<th className="hidden">ID</th>
								<th className="">Course Name</th>
								<th className={role == UserRole.Writer ? "hidden" : ""}>Grade</th>
								<th className={!showSchools ? "hidden" : ""}>School</th>
								<th className="">Status</th>
								<th className="">Created</th>
								<th className="">Course Code</th>
							</tr>
						</thead>
						{filteredCourseRegistrations?.length == 0 ? (
							<NoDataTableBody
								columnCount={7}
								message="You have no courses available."
							/>
						) : (
							<tbody>
								{courseRegistrations &&
									filteredCourseRegistrations?.map((course) => (
										<tr key={course.courseId} className="">
											<td className="hidden">{course.courseId}</td>
											<td className="">
												<a
													id={`choose-course-${course.courseId}`}
													className="cursor-pointer"
													onClick={() =>
														handleSelectCourse(
															course.courseId,
															course.courseName,
															course.courseGrade,
															course.status
														)
													}>
													{course.courseName}
												</a>
											</td>
											<td className={role == UserRole.Writer ? "hidden" : ""}>
												{course.courseGrade}
											</td>
											<td className={!showSchools ? "hidden" : ""}>
												{course.school}
											</td>
											<td className="">{course.status}</td>
											<td className="">{course.createDate}</td>
											<td className="">{course.courseReferralCode}</td>
										</tr>
									))}
							</tbody>
						)}
					</table>
				)}

				{view == "dashboard" && (
					<>
						<Loading loading={dashboardResult.isLoading} />
						{dashboardCourses && (
							<div className="mb-8">
								<StudentDashboard showAll={showAll} cards={dashboardCourses} />
							</div>
						)}
					</>
				)}
			</section>
			{(view == "table" || view == "dashboard") && role === UserRole.Writer && (
				<section id="course-code-signup" className="mt-8">
					<Form className="space-y-6" onSubmit={handleRegister} method="post">
						<h2>Want to Sign Up for Another Course?</h2>
						<p>
							Enter the course you have been given and click <b>Register</b>.
						</p>
						<div>
							<label
								htmlFor="courseCode"
								className="block text-sm font-medium text-gray-700">
								Course Code
							</label>
							<input
								value={courseCode}
								type="text"
								name="courseCode"
								id="courseCode"
								className="block w-64 rounded-md border-gray-300 pr-10 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
								placeholder="Course code"
								required
								onChange={(e) => setCourseCode(e.target.value)}
							/>
							<InputError error={courseCodeError}></InputError>
						</div>

						<button
							id="register-button"
							type="submit"
							className="btn btn-primary"
							disabled={courseCode == ""}>
							Register
						</button>
					</Form>
				</section>
			)}

			{(view == "table" || view == "dashboard") && (
				<section className="min-h-full justify-center py-8 sm:px-6 lg:px-8">
					<div className="text-center">
						<a
							id="switch-site"
							href={`${aspUrl}/SignIn/SelectCourse`}
							onClick={() => sessionStorage.setItem("prefersSpa", "false")}
							className="text-lg text-primary-700 hover:text-primary-600 text-center">
							Want to return to {config?.siteName} Classic?
						</a>
					</div>
				</section>
			)}

			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Courses" result={coursesResult} />
					<RtkqResultViewer heading="Select Course" result={selectCoursePageResult} />
					<RtkqResultViewer heading="Dashboard" result={dashboardResult} />
				</>
			)}
		</>
	);
}

function Greeting(props: {
	role: UserRole | null;
	view: string;
	hasCourse: boolean;
	showInactiveCourses: boolean;
	showAddCourseLink: boolean;
}) {
	const { role, view, hasCourse, showInactiveCourses, showAddCourseLink } = props;

	if (role === UserRole.Writer)
		return (
			<StudentGreeting
				view={view}
				hasCourse={hasCourse}
				showInactiveCourses={showInactiveCourses}
				showAddCourseLink={showAddCourseLink}
			/>
		);
	if (role === UserRole.Proctor)
		return (
			<TeacherGreeting
				view={view}
				hasCourse={hasCourse}
				showInactiveCourses={showInactiveCourses}
				showAddCourseLink={showAddCourseLink}
			/>
		);
	return <p>Choose a course to get started.</p>;
}

function StudentGreeting(props: {
	view: string;
	hasCourse: boolean;
	showInactiveCourses: boolean;
	showAddCourseLink: boolean;
}) {
	const { view, hasCourse, showInactiveCourses, showAddCourseLink } = props;
	const AddCourseText = () => {
		return (
			<>
				{" "}
				If you need to sign up for a different course, you can do so{" "}
				<a className="cursor-pointer" onClick={() => scrollToSection("course-code-signup")}>
					at the bottom
				</a>{" "}
				of this page.
			</>
		);
	};
	if (view === "table") {
		if (!hasCourse)
			return (
				<p className="mt-2">
					You are not enrolled in any courses. To sign up for a course, you can do so
					below.
				</p>
			);
		else
			return (
				<p>
					You are enrolled in the following courses. Click a course name to access its
					home page where you can write a practice essay, run lessons, and view your
					reports. <AddCourseText />
				</p>
			);
	} else if (view === "dashboard") {
		if (!hasCourse)
			return (
				<p className="mt-2">
					You are not enrolled in any courses. To sign up for a course, you can do so
					below.
				</p>
			);
		else
			return (
				<p>
					You are enrolled in the following courses.{" "}
					{showInactiveCourses
						? "Click View Reports to see the reports for your inactive course."
						: "Click an assignment to get right to work, or click a course name to access the home page."}
					<AddCourseText />
				</p>
			);
	}
}

function TeacherGreeting(props: {
	view: string;
	hasCourse: boolean;
	showInactiveCourses: boolean;
	showAddCourseLink: boolean;
}) {
	const { view, hasCourse, showInactiveCourses, showAddCourseLink } = props;
	const AddCourseText = () => {
		if (showAddCourseLink)
			return (
				<>
					{" "}
					You can create a new course by clicking the <strong>Add Course</strong> button.
				</>
			);
	};

	if (!hasCourse)
		return (
			<p className="mt-2">
				You are not registered to teach any courses.
				<AddCourseText />
			</p>
		);

	if (view === "table")
		return (
			<p>
				You are registered to teach the following courses. Click a course name to access its
				home page.
				<AddCourseText />
			</p>
		);

	if (view === "dashboard")
		return (
			<p>
				You are registered to teach the following courses.
				{showInactiveCourses
					? " Click View Reports to see the reports for your inactive course."
					: " Click an assignment to get right to work, or click a course name to access the home page."}
				<AddCourseText />
			</p>
		);
}

function InputError(props: { error: string }) {
	if (props.error) {
		return (
			<div
				className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4"
				role="alert">
				<p>{props.error}</p>
			</div>
		);
	}
	return <></>;
}
